import type { NodeId } from '../../lib/types';
import type { SliceCreator } from '../state';

/**
 * Where each node sits, kept apart from the node itself.
 *
 * These four numbers are SERVER state, not hints — `autoLayout()` derives them from measured
 * DOM and `pushStructuralSync()` persists them, and CLAUDE.md makes round-tripping them
 * identically one of the port's verification gates.
 *
 * WHY A SEPARATE SLICE. Two reasons, both about the drag path. The first is subscription
 * width: `MapNode` must be able to watch its own position and nothing else, or one node moving
 * re-renders all 147. The second is write cost: a drag commit sets coordinates and must not
 * have to clone a node object carrying a label, tags, four doc ids and a description to do it.
 */
export interface NodePosition {
  x: number;
  y: number;
  /** -1 left of the root, +1 right. */
  side: number;
  /** Distance from the root, in columns. */
  depth: number;
}

export type PositionMap = Record<NodeId, NodePosition>;

export interface PositionsSlice {
  positions: PositionMap;

  /**
   * The EPHEMERAL y of each node while the documentation rows are expanded.
   *
   * Only the nodes the display pass actually placed have an entry, and `posY()` prefers an
   * entry whenever there is one — which is why a stray entry silently moves a node and why
   * `displayLayout()` must not invent any. Nothing here is ever persisted: zooming back out
   * empties it and the canonical `positions` above are what survive a reload.
   */
  displayY: Record<NodeId, number>;
  /** Whether the documentation rows are expanded — the zoom-threshold state. */
  docsExpanded: boolean;
  /**
   * A request for a layout pass, counted rather than flagged.
   *
   * WHY A COUNTER IN THE STORE AND A QUEUE OUTSIDE IT. Layout cannot run at the moment it is
   * asked for: expanding a branch makes nodes visible that React has not rendered yet, so
   * their heights are still unknown and the pass would place them at the 46px fallback. So
   * `relayout()` records WHAT to do in `features/map/mapController.ts`'s queue and bumps this,
   * and `MapStage` drains the queue in a layout effect — after the render, after the
   * measurement, and still before paint. The counter is what makes the render happen; the
   * queue is what remembers the ops, because ops are not a value a component should diff.
   */
  layoutTick: number;

  /**
   * Replace the whole position map, PRESERVING IDENTITY where nothing moved.
   *
   * This is the exception to the container-identity rule the graph slice states, and it is
   * allowed here for a specific reason: a position is four primitives, so "did it change" can
   * be answered by comparing values instead of inferring it from identity. That matters
   * because the mirror runs on every `sync()` — which is every vanilla `render()` — and a
   * fresh object per node per sync would make `s => s.positions[id]` return something new
   * every time, re-rendering every node on any change to anything.
   *
   * Returns nothing, and deliberately does not touch the container at all when every entry
   * is unchanged: `positions` keeps its identity, so a whole-map selector stays quiet too.
   */
  setPositions: (next: PositionMap) => void;

  /** One node's position — what a drag commit writes on `pointerup`. */
  setPosition: (id: NodeId, next: NodePosition) => void;

  /** The display pass's answer, or `{}` when the rows collapse again. */
  setDisplayY: (next: Record<NodeId, number>) => void;
  setDocsExpanded: (on: boolean) => void;
  /** Ask for the queued layout ops to be drained on the next commit. */
  bumpLayoutTick: () => void;
}

/** Same four numbers? Written out rather than looped, because it runs per node per sync. */
export function samePosition(a: NodePosition | undefined, b: NodePosition | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.x === b.x && a.y === b.y && a.side === b.side && a.depth === b.depth;
}

export const createPositionsSlice: SliceCreator<PositionsSlice> = (set, get) => ({
  positions: {},
  displayY: {},
  docsExpanded: false,
  layoutTick: 0,

  setDisplayY: (next) => {
    const prev = get().displayY;
    // Both empty is the common case — the rows are collapsed and every render would
    // otherwise hand out a fresh `{}` and re-render every node.
    const prevKeys = Object.keys(prev);
    const nextKeys = Object.keys(next);
    if (prevKeys.length === nextKeys.length && nextKeys.every((k) => prev[k] === next[k])) return;
    set({ displayY: next });
  },

  setDocsExpanded: (on) => {
    if (get().docsExpanded === on) return;
    set({ docsExpanded: on });
  },

  bumpLayoutTick: () => set({ layoutTick: get().layoutTick + 1 }),

  setPositions: (next) => {
    const prev = get().positions;
    let changed = false;
    const merged: PositionMap = {};
    for (const id in next) {
      const before = prev[id];
      if (samePosition(before, next[id])) {
        merged[id] = before;
      } else {
        merged[id] = next[id];
        changed = true;
      }
    }
    // A node that has gone is a change even though no surviving entry moved.
    if (!changed) {
      for (const id in prev) {
        if (!(id in next)) {
          changed = true;
          break;
        }
      }
    }
    if (changed) set({ positions: merged });
  },

  setPosition: (id, next) => {
    const prev = get().positions;
    if (samePosition(prev[id], next)) return;
    set({ positions: { ...prev, [id]: next } });
  },
});
